import { action, computed, observable } from "mobx";
import Provider from "~/models/Provider";
import ProviderRepository from "~/repository/Provider/ProviderRepository";
import NestStore from "~/stores/NestStore";

class ProviderStore extends NestStore {
  /**
   * @property {Provider}
   */
  @observable provider;

  @observable needReload;

  /**
   * @private
   */
  #providerCopy;

  /**
   * @property {number}
   */
  @observable providerId;

  providerRepository = null;

  constructor() {
    super();
    this.provider = new Provider({});
    this.providerRepository = new ProviderRepository();
  }

  /**
   * Return if Provider has unsaved data or not
   * @returns {boolean}
   */
  @computed
  get hasUnsavedData() {
    return this.provider.isDirty;
  }

  @action
  reset() {
    this.clearErrorMessages();
    this.provider = new Provider(this.#providerCopy);
  }

  @action
  load(providerId = null) {
    if (providerId) {
      this.providerId = providerId;
    }

    this.isLoading = true;

    return Promise.all([this.getProvider()]).then(() => {
      this.needReload = false;
      this.isLoading = false;
    });
  }

  @action
  loadIndex() {
    this.isLoading = true;

    return Promise.all([this.getAll()]).finally(() => {
      this.isLoading = false;
    });
  }

  /**
   * Get all.
   */
  @action
  getAll() {
    const params = {
      offset: this.offset,
      limit: this.pagination.itemsPerPage,
    };

    if (this.searchParams.providerId !== "") {
      params.providerId = this.searchParams.providerId;
    }

    return this.providerRepository.fetch(params).then(response => {
      this.processEntities(response.data.providers);
      this.processPagination(response.data.pagination);
    });
  }

  @action
  processEntities(providers = []) {
    this.providers = [];

    providers.forEach(providerArray => {
      const provider = new Provider(providerArray);

      this.providers.push(provider);
    });
  }

  @action
  getProvider() {
    return this.providerRepository.fetchOne(this.providerId).then(response => {
      this.provider = new Provider(response.data.provider);
    });
  }

  @action
  save = data => this.saveProvider(this.providerId, data);

  /**
   * @private
   */
  saveProvider(providerId, data) {
    this.isSaving = true;
    this.clearErrorMessages();
    return this.providerRepository
      .update(providerId, data)
      .then(response => {
        if (response.status === "success" && response.data.provider) {
          this.provider = new Provider(response.data.provider);
          this.#providerCopy = response.data.provider;
        }
        this.isSaving = false;
      })
      .catch(e => {
        this.isSaving = false;
        this.setErrorMessages(e);
        throw e;
      });
  }

  @computed
  get actualUser() {
    return this.provider;
  }
}

export default ProviderStore;
